import { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Stack,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Badge,
    Button,
    Popover,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router';
import {
    Search,
    CloseOutlined,
    FilterListOutlined,
    RestartAlt,
    FileUploadOutlined,
    UploadFileOutlined,
    HistoryOutlined,
    PersonAddOutlined,
    ReceiptLongOutlined,
    FileDownloadOutlined
} from '@mui/icons-material';

const CreditLimitsToolbar = ({
    filteredCount,
    totalCount,
    hasActiveFilters,
    searchTerm,
    onSearchChange,
    statusFilter,
    onStatusFilterChange,
    typeFilter,
    onTypeFilterChange,
    onClearFilters,
    onOpenUploadModal,
    onOpenOutstandingModal,
    onOpenHistoryModal,
    onOpenOnboardModal,
    onNavigateTransactionHistory,
    onExport,
    isExporting = false
}) => {
    const navigate = useNavigate();
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const handleTransactionHistoryClick = () => {
        if (onNavigateTransactionHistory) {
            onNavigateTransactionHistory();
        } else {
            navigate('/HR/transaction-history');
        }
    };

    return (
        <Box
            sx={{
                px: 3,
                pt: 2.5,
                pb: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2
            }}
        >
            {/* Title & Subtitle Badge */}
            <Box>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="h4" fontWeight={700} color="text.primary">
                        Credit Limits
                    </Typography>
                    {hasActiveFilters && (
                        <Chip
                            label="Filtered"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                        />
                    )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                    Showing {filteredCount} of {totalCount} accounts
                </Typography>
            </Box>

            {/* Controls Row: Search on Left, Buttons on Right */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    width: '100%'
                }}
            >
                {/* Search Input (Left Aligned) */}
                <TextField
                    placeholder="Search Name, Email, status..."
                    size="small"
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    sx={{
                        width: { xs: '100%', sm: 260 },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: '#f8fafc',
                            '& fieldset': { borderColor: 'divider' }
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: 'text.disabled', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => onSearchChange('')}>
                                    <CloseOutlined sx={{ fontSize: 16 }} />
                                </IconButton>
                            </InputAdornment>
                        ) : null
                    }}
                />

                {/* Action Buttons (Right Aligned) */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                        ml: 'auto'
                    }}
                >
                    {/* Filter Button */}
                    <Tooltip title="Filter Accounts">
                        <Badge color="primary" variant="dot" invisible={!hasActiveFilters}>
                            <Button
                                variant="outlined"
                                startIcon={<FilterListOutlined />}
                                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: hasActiveFilters ? 'primary.main' : 'divider',
                                    color: hasActiveFilters ? 'primary.main' : 'text.primary',
                                    bgcolor: hasActiveFilters ? '#eff6ff' : '#fff',
                                    '&:hover': { bgcolor: '#f8fafc' }
                                }}
                            >
                                Filter
                            </Button>
                        </Badge>
                    </Tooltip>

                    {/* Filter Dropdown Popover */}
                    <Popover
                        open={Boolean(filterAnchorEl)}
                        anchorEl={filterAnchorEl}
                        onClose={() => setFilterAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                            sx: { p: 2.5, width: 280, borderRadius: 2, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }
                        }}
                    >
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                            Filter Accounts
                        </Typography>

                        <Stack spacing={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select value={statusFilter} label="Status" onChange={(e) => onStatusFilterChange(e.target.value)}>
                                    <MenuItem value="All">All Statuses</MenuItem>
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>Account Type</InputLabel>
                                <Select value={typeFilter} label="Account Type" onChange={(e) => onTypeFilterChange(e.target.value)}>
                                    <MenuItem value="All">All Types</MenuItem>
                                    <MenuItem value="HR Employee">HR Employee</MenuItem>
                                    <MenuItem value="Corporate User">Corporate User</MenuItem>
                                </Select>
                            </FormControl>

                            <Divider />

                            <Stack direction="row" spacing={1} justifyContent="space-between">
                                <Button
                                    size="small"
                                    startIcon={<RestartAlt fontSize="small" />}
                                    onClick={() => {
                                        onClearFilters();
                                        setFilterAnchorEl(null);
                                    }}
                                    disabled={!hasActiveFilters}
                                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                                >
                                    Reset
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => setFilterAnchorEl(null)}
                                    sx={{ textTransform: 'none', borderRadius: 1.5 }}
                                >
                                    Apply
                                </Button>
                            </Stack>
                        </Stack>
                    </Popover>

                    {/* Export Button */}
                    <Tooltip title="Export to Excel">
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={
                                    isExporting ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <FileDownloadOutlined sx={{ fontSize: '18px !important' }} />
                                    )
                                }
                                onClick={onExport}
                                disabled={isExporting}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: 'divider',
                                    color: 'text.primary',
                                    bgcolor: '#fff',
                                    '&:hover': { bgcolor: '#f8fafc', borderColor: 'primary.main' }
                                }}
                            >
                                {isExporting ? 'Exporting...' : 'Export'}
                            </Button>
                        </span>
                    </Tooltip>

                    {/* Upload Limits Button */}
                    <Button
                        startIcon={<FileUploadOutlined sx={{ fontSize: '18px !important' }} />}
                        variant="outlined"
                        color="primary"
                        onClick={onOpenUploadModal}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: 'divider',
                            color: 'text.primary',
                            bgcolor: '#fff',
                            '&:hover': { bgcolor: '#f8fafc', borderColor: 'primary.main' }
                        }}
                    >
                        Upload Limits
                    </Button>

                    {/* Upload Outstanding Button */}
                    <Button
                        startIcon={<UploadFileOutlined sx={{ fontSize: '18px !important' }} />}
                        variant="outlined"
                        color="primary"
                        onClick={onOpenOutstandingModal}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: 'divider',
                            color: 'text.primary',
                            bgcolor: '#fff',
                            '&:hover': { bgcolor: '#f8fafc', borderColor: 'primary.main' }
                        }}
                    >
                        Upload Outstanding
                    </Button>

                    {/* History Button */}
                    <Button
                        startIcon={<HistoryOutlined sx={{ fontSize: '18px !important' }} />}
                        variant="outlined"
                        color="primary"
                        onClick={onOpenHistoryModal}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: 'divider',
                            color: 'text.primary',
                            bgcolor: '#fff',
                            '&:hover': { bgcolor: '#f8fafc', borderColor: 'primary.main' }
                        }}
                    >
                        History
                    </Button>

                    {/* Primary Action Button: [+ Onboard Employee] */}
                    <Button
                        startIcon={<PersonAddOutlined sx={{ fontSize: '18px !important' }} />}
                        variant="contained"
                        color="primary"
                        onClick={onOpenOnboardModal}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: '#1e40af',
                            boxShadow: '0 4px 12px rgba(30, 64, 175, 0.25)',
                            '&:hover': { bgcolor: '#1d4ed8' }
                        }}
                    >
                        Onboard Employee
                    </Button>

                    {/* Transaction History Button (Right side of Onboard Employee) */}
                    <Button
                        startIcon={<ReceiptLongOutlined sx={{ fontSize: '18px !important' }} />}
                        variant="outlined"
                        onClick={handleTransactionHistoryClick}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: '#1e40af',
                            color: '#1e40af',
                            bgcolor: '#eff6ff',
                            '&:hover': { bgcolor: '#dbeafe', borderColor: '#1d4ed8' }
                        }}
                    >
                        Transaction History
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

CreditLimitsToolbar.propTypes = {
    filteredCount: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
    hasActiveFilters: PropTypes.bool.isRequired,
    searchTerm: PropTypes.string.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    statusFilter: PropTypes.string.isRequired,
    onStatusFilterChange: PropTypes.func.isRequired,
    typeFilter: PropTypes.string.isRequired,
    onTypeFilterChange: PropTypes.func.isRequired,
    onClearFilters: PropTypes.func.isRequired,
    onOpenUploadModal: PropTypes.func.isRequired,
    onOpenOutstandingModal: PropTypes.func,
    onOpenHistoryModal: PropTypes.func.isRequired,
    onOpenOnboardModal: PropTypes.func.isRequired,
    onNavigateTransactionHistory: PropTypes.func,
    onExport: PropTypes.func,
    isExporting: PropTypes.bool
};

export default CreditLimitsToolbar;
